import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import RuleIcon from "@mui/icons-material/Rule";
import PlayCircleSharpIcon from '@mui/icons-material/PlayCircleSharp';
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from "@mui/icons-material/History";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { MdCleaningServices } from "react-icons/md";
import { ROLES, hasAnyRole } from "../utils/roles";

export default function AppDrawer({
  drawerWidth,
  mobileOpen,
  onClose,
  collapsed,
}) {
  const location = useLocation();
  const currentStoreId = new URLSearchParams(location.search).get("storeId") || localStorage.getItem("selected-store-id");
  const userRoles = useSelector((state) => state.globalData.userRoles);

  const menuItems = [
    { label: "Pricing Rules", path: "/", icon: <RuleIcon />, allowedRoles: [ROLES.PRICING_USER] },
    {
      label: "Pricing Rule Simulator",
      path: "/pricing-rule-simulator",
      icon: <PlayCircleSharpIcon />,
      allowedRoles: [ROLES.PRICING_USER, ROLES.STORE_MANAGER],
    },
    {
      label: "Liquidation Sale",
      path: currentStoreId
        ? `/liquidation-sale-products?storeId=${encodeURIComponent(currentStoreId)}`
        : "/liquidation-sale-products",
      icon: <MdCleaningServices size={"2em"} />,
      allowedRoles: [ROLES.STORE_MANAGER],
    },
    {
      label: "Liquidation Sale Discount",
      path: "/liquidation-sale-discount",
      icon: (
        <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          <MdCleaningServices size={"1.7em"} />
          <SettingsIcon sx={{ fontSize: 18, position: "absolute", bottom: -6, right: -8 }} />
        </Box>
      ),
      allowedRoles: [ROLES.PRICING_USER],
    },
    {
      label: "Flash sale - Product sort order",
      path: currentStoreId
        ? `/flash-sale-product-sort-order?storeId=${encodeURIComponent(currentStoreId)}`
        : "/flash-sale-product-sort-order",
      icon: <SwapVertIcon />,
      allowedRoles: [ROLES.PRICING_USER],
    },
    {
      label: "Audit",
      path: "/audit",
      icon: <HistoryIcon />,
      allowedRoles: [ROLES.RULE_AUDIT],
    },
  ].filter((item) => hasAnyRole(userRoles, item.allowedRoles));

  const handleLogout = () => {
    localStorage.clear();
    // Send the post-login landing spot through as `redirect`, same param
    // name VITE_API_LOGIN_URL already uses - otherwise logging back in
    // lands wherever the IDP defaults to instead of the app's home page.
    const homeUrl = `${window.location.origin}/`;
    window.location.href = `${import.meta.env.VITE_API_LOGOUT_URL}?redirect=${encodeURIComponent(homeUrl)}`;
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* IMPORTANT: this toolbar gives spacing below topbar */}
      <Toolbar />

      <Divider />

      <List sx={{ px: 1, pt: 3 }}>
        {menuItems.map((item) => (
          <Tooltip
            key={item.path}
            title={collapsed ? item.label : ""}
            placement="right"
            arrow
          >
            <ListItemButton
              component={NavLink}
              to={item.path}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                justifyContent: collapsed ? "center" : "flex-start",
                "&.active": {
                  background: "rgba(25, 118, 210, 0.12)",
                  color: "#1976d2",
                },
                "&.active .MuiListItemIcon-root": {
                  color: "#1976d2",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? "auto" : 40,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>

              {!collapsed && <ListItemText primary={item.label} />}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>

      <Box sx={{ mt: "auto", p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="error"
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            py: 1.2,
            minWidth: 0,
          }}
          startIcon={!collapsed ? <LogoutIcon /> : null}
        >
          {collapsed ? <LogoutIcon /> : "Logout"}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: 260 },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            transition: "width 0.25s ease",
            overflowX: "hidden",
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}
